import { useMemo, useState } from "react";
import { track } from "@/lib/analytics";
import { toCsv, type AccessReportSpecialty } from "@/lib/access-report";

type SortKey =
  | "specialty"
  | "listed"
  | "withWebsite"
  | "withPhone"
  | "confirmed"
  | "accepting"
  | "waitReports";

const COLUMNS: Array<{ key: SortKey; label: string }> = [
  { key: "specialty", label: "Specialty" },
  { key: "listed", label: "Listed" },
  { key: "withWebsite", label: "Website" },
  { key: "withPhone", label: "Phone" },
  { key: "confirmed", label: "Confirmed" },
  { key: "accepting", label: "Accepting" },
  { key: "waitReports", label: "Wait reports" },
];

export function SpecialtyTable({
  bySpecialty,
  slug,
}: {
  bySpecialty: AccessReportSpecialty[];
  slug: string;
}) {
  const [sortKey, setSortKey] = useState<SortKey>("listed");
  const [direction, setDirection] = useState<"asc" | "desc">("desc");
  const sortedRows = useMemo(() => {
    const rows = [...bySpecialty];
    rows.sort((left, right) => {
      const leftValue = sortValue(left, sortKey);
      const rightValue = sortValue(right, sortKey);
      const comparison = typeof leftValue === "string"
        ? leftValue.localeCompare(String(rightValue), "en-AU")
        : leftValue - Number(rightValue);
      return direction === "asc" ? comparison : -comparison;
    });
    return rows;
  }, [bySpecialty, direction, sortKey]);
  const csvHref = `data:text/csv;charset=utf-8,${encodeURIComponent(toCsv(bySpecialty))}`;

  const selectSort = (key: SortKey) => {
    if (sortKey === key) {
      setDirection((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setDirection(key === "specialty" ? "asc" : "desc");
  };

  return (
    <>
      <div className="pb-access-table-actions">
        <p>Counts are frozen at the report compute date.</p>
        <a
          href={csvHref}
          download={`${slug}-by-specialty.csv`}
          onClick={() => track("access_report_csv", { slug })}
        >
          Download CSV
        </a>
      </div>
      <div className="pb-access-table-wrap">
        <table className="pb-access-specialty-table">
          <thead>
            <tr>
              {COLUMNS.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  aria-sort={sortKey === column.key
                    ? direction === "asc" ? "ascending" : "descending"
                    : "none"}
                >
                  <button type="button" onClick={() => selectSort(column.key)}>
                    {column.label}
                    {sortKey === column.key ? <span aria-hidden="true">{direction === "asc" ? " ↑" : " ↓"}</span> : null}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row) => (
              <tr key={row.specialty}>
                <th scope="row">{row.specialty}</th>
                <td>{row.listed}</td>
                <td>{row.withWebsite}</td>
                <td>{row.withPhone}</td>
                <td>{row.confirmed}</td>
                <td>{row.accepting}</td>
                <td>{row.waitReports?.n ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function sortValue(row: AccessReportSpecialty, key: SortKey): string | number {
  if (key === "waitReports") return row.waitReports?.n ?? -1;
  return row[key];
}
